import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Sparkles, Target, Search, ThumbsUp, ThumbsDown, Check, X, Network, ShieldCheck, FlaskConical, Activity, Clock, GitCompareArrows, HelpCircle, ListChecks, Lightbulb } from "lucide-react";
import { api } from "../../services/api";
import AnimatedLineChart from "../charts/AnimatedLineChart";

function Mark({ shape, color }) { return <span className={`type-mark ${shape}`} style={{ "--mk": color }} />; }

const SEV = {
  Critical: { color: "#dc2626", bg: "#fef2f2", ring: "#fecaca" },
  High: { color: "#ea580c", bg: "#fff7ed", ring: "#fed7aa" },
  Medium: { color: "#ca8a04", bg: "#fefce8", ring: "#fde68a" },
  Low: { color: "#64748b", bg: "#f8fafc", ring: "#e2e8f0" },
};

export default function IGLDecisionCard({ d, defaultOpen }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fb, setFb] = useState({ verdict: null, note: "" });
  const [noteOpen, setNoteOpen] = useState(false);
  const [disabled, setDisabled] = useState([]);
  const [whatif, setWhatif] = useState(null);
  const nav = useNavigate();
  const cm = d.category_meta;

  useEffect(() => {
    if (open && !detail) {
      setLoading(true);
      api.iglDecision(d.id).then((x) => { setDetail(x); setLoading(false); });
      api.iglGetFeedback(d.id).then((f) => setFb({ verdict: f.verdict || null, note: f.note || "" }));
    }
  }, [open]); // eslint-disable-line

  const toggleSignal = async (sid) => {
    const next = disabled.includes(sid) ? disabled.filter((x) => x !== sid) : [...disabled, sid];
    setDisabled(next);
    if (next.length === 0) { setWhatif(null); return; }
    const r = await api.iglRecompute(d.id, next);
    setWhatif(r);
  };

  const setVerdict = async (verdict) => {
    const next = { ...fb, verdict };
    setFb(next);
    await api.iglFeedback({ decision_id: d.id, verdict, note: next.note });
  };
  const saveNote = async () => {
    await api.iglFeedback({ decision_id: d.id, verdict: fb.verdict, note: fb.note });
    setNoteOpen(false);
  };

  const score = whatif ? whatif.score : d.score;
  const severity = whatif ? whatif.severity : d.severity;
  const confidence = whatif ? whatif.confidence : d.confidence;
  const rank = whatif ? whatif.rank : d.rank;
  const sev = SEV[severity] || SEV.Medium;

  return (
    <article className={`decision-card ${open ? "open" : ""}`} style={{ "--accent": cm.color }}>
      <div className="dc-accent" />
      <button className="dc-head" onClick={() => setOpen((v) => !v)}>
        <span className="dc-rank">#{rank}</span>
        <span className="type-pill lg" style={{ background: cm.soft, color: cm.text }}>
          <Mark shape={cm.shape} color={cm.color} /> {d.category_label}
        </span>
        <div className="dc-head-main">
          <div className="dc-title">{d.title}</div>
          <div className="dc-headline">{d.entity_name} <span className="dot-sep">·</span> {d.region} <span className="dot-sep">·</span> {d.source_count} systems correlated</div>
        </div>
        <div className="dc-right">
          <span className="sev-badge" style={{ background: sev.bg, color: sev.color, borderColor: sev.ring }}>{severity}</span>
          <div className="risk-meter" title="Risk score">
            <div className="risk-fill" style={{ width: `${score}%`, background: sev.color }} />
          </div>
          <div className="dc-conf">score <b>{score}</b> · {confidence}% conf</div>
        </div>
        <ChevronDown size={18} className={`chev ${open ? "open" : ""}`} />
      </button>

      {open && (
        <div className="dc-body">
          {loading || !detail ? (
            <div className="insight-loading">Generating explanation…</div>
          ) : (
            <>
              <div className="ai-block">
                <div className="ai-row">
                  <div className="ai-icon"><Sparkles size={15} /></div>
                  <div><div className="ai-label">Why it matters</div><p>{detail.explanation?.why_it_matters}</p></div>
                </div>
                <div className="ai-row">
                  <div className="ai-icon rec"><Target size={15} /></div>
                  <div><div className="ai-label">Recommended action</div><p>{detail.explanation?.recommended_action}</p></div>
                </div>
                <div className="ai-row">
                  <div className="ai-icon"><Search size={15} /></div>
                  <div><div className="ai-label">Why this confidence</div><p>{detail.explanation?.confidence_rationale}</p></div>
                </div>
              </div>

              {/* Section 22 — Explainable Insights */}
              {detail.explainable && (
                <div className="xai">
                  <div className="xai-head"><Sparkles size={14} /> Explainable Insight</div>

                  <div className="xai-why"><HelpCircle size={15} /><div><span className="xai-lbl">Why am I seeing this?</span>{detail.explainable.why_seeing}</div></div>

                  <div className="xai-cols">
                    <div className="xai-col facts">
                      <div className="xai-col-h"><ListChecks size={13} /> Observed facts</div>
                      <ul>{detail.explainable.facts.map((f, i) => <li key={i}>{f}</li>)}</ul>
                    </div>
                    <div className="xai-col infer">
                      <div className="xai-col-h"><Network size={13} /> Inferred relationships</div>
                      <ul>{detail.explainable.inferences.map((f, i) => <li key={i}>{f}</li>)}</ul>
                    </div>
                    <div className="xai-col rec">
                      <div className="xai-col-h"><Lightbulb size={13} /> Recommendation</div>
                      <p>{detail.explainable.recommendation}</p>
                    </div>
                  </div>

                  <div className="xai-conf">
                    <div className="xai-col-h">Confidence breakdown</div>
                    {detail.explainable.confidence_breakdown.map((c) => (
                      <div key={c.factor} className="conf-factor">
                        <div className="cf-top"><span>{c.factor}</span><b>{c.level}%</b></div>
                        <div className="cf-bar"><div className="cf-fill" style={{ width: `${c.level}%` }} /></div>
                        <div className="cf-detail">{c.detail}</div>
                      </div>
                    ))}
                  </div>

                  <div className="xai-time">
                    <div className="xai-col-h"><Clock size={13} /> Time-based reasoning</div>
                    <div className="tr-grid">
                      <div><span>What changed</span>{detail.explainable.time_reasoning.what_changed}</div>
                      <div><span>When</span>{detail.explainable.time_reasoning.when}</div>
                      <div><span>Why now</span>{detail.explainable.time_reasoning.why_now}</div>
                    </div>
                  </div>

                  {detail.explainable.conflicts && detail.explainable.conflicts.length > 0 && (
                    <div className="xai-conflict">
                      <div className="xai-col-h warn"><GitCompareArrows size={13} /> Conflicting evidence — surfaced, not hidden</div>
                      {detail.explainable.conflicts.map((c, i) => (
                        <div key={i} className="conflict-row">
                          <div className="conflict-srcs"><span>{c.source_a}</span><GitCompareArrows size={12} /><span>{c.source_b}</span></div>
                          <p>{c.note}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Interactive what-if signal toggles */}
              <div className="whatif">
                <div className="whatif-head">
                  <Activity size={14} /> Contributing signals — <span>toggle a signal off to see the risk score & rank recompute live</span>
                </div>
                <div className="signal-list">
                  {detail.signals.map((s) => {
                    const off = disabled.includes(s.id);
                    return (
                      <button key={s.id} className={`signal-row ${off ? "off" : ""}`} onClick={() => toggleSignal(s.id)}>
                        <span className="sig-toggle">{off ? <X size={13} /> : <Check size={13} />}</span>
                        <span className="sig-weight" style={{ opacity: off ? 0.4 : 1 }}>+{s.weight}</span>
                        <span className="sig-main">
                          <span className="sig-label">{s.label} <span className="sig-src">{s.source}</span></span>
                          <span className="sig-detail">{s.detail}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
                {whatif && (
                  <div className="whatif-result">
                    Risk score <b>{whatif.prev_score} → {whatif.score}</b> · severity <b>{whatif.prev_severity} → {whatif.severity}</b> · rank <b>#{whatif.prev_rank} → #{whatif.rank}</b>
                    <button className="reset-whatif" onClick={() => { setDisabled([]); setWhatif(null); }}>reset</button>
                  </div>
                )}
              </div>

              <div className="reasoning-path">
                <div className="rp-label">Relationship path</div>
                <div className="rp-chain">
                  {detail.path_nodes.map((n, i) => (
                    <React.Fragment key={n.id}>
                      <span className={`rp-node t-${n.type}`}>{n.label}</span>
                      {i < detail.path_nodes.length - 1 && <span className="rp-arrow">→</span>}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {detail.telemetry && (
                <div className="chart-card">
                  <div className="sub-chart-title"><Activity size={13} /> {detail.telemetry.label} ({detail.telemetry.unit}) — safe-max {detail.telemetry.safe_max}</div>
                  <AnimatedLineChart
                    series={[{ key: "pressure", label: detail.telemetry.label, color: "#dc2626", values: detail.telemetry.values }]}
                    labels={detail.telemetry.dates}
                  />
                </div>
              )}

              {detail.timeline && detail.timeline.length > 0 && (
                <div className="timeline-card">
                  <div className="sub-chart-title"><Activity size={13} /> Evidence timeline — how this built up</div>
                  <div className="timeline">
                    {detail.timeline.map((t, i) => (
                      <div key={i} className={`tl-item k-${t.kind}`}>
                        <div className="tl-marker"><span className="tl-dot" /></div>
                        <div className="tl-content">
                          <div className="tl-top">
                            <span className="tl-date">{t.time}</span>
                            <span className="tl-ago">{t.days_ago === 0 ? "today" : `${t.days_ago}d ago`}</span>
                            <span className="tl-src">{t.source}</span>
                            <span className="tl-rec">{t.record_id}</span>
                          </div>
                          <div className="tl-text">{t.text}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="evidence-grid">
                <div className="ev-side">
                  <div className="ev-h"><FlaskConical size={13} /> Source evidence (provenance)</div>
                  <ul className="ev-list">
                    {detail.evidence.map((e, i) => (
                      <li key={i}>
                        <span className="ev-src">{e.source}</span>
                        <span className="ev-what">{e.text}</span>
                        <span className="ev-conf">{e.record_id}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="ev-side">
                  <div className="ev-h"><ShieldCheck size={13} /> Provenance & audit</div>
                  <div className="prov">
                    <div><span>Decision ID</span><b>{d.id}</b></div>
                    <div><span>Rule version</span><b>{detail.rule_version}</b></div>
                    <div><span>Generated</span><b>{detail.generated_at}</b></div>
                    <div><span>Evidence records</span><b>{detail.evidence.length}</b></div>
                    <div><span>Guardrail</span><b>Human approval required</b></div>
                  </div>
                </div>
              </div>

              <div className="insight-actions">
                <button className="btn-ghost sm" onClick={() => nav(`/igl/graph?focus=${d.entity_id}`)}>
                  <Network size={15} /> View relationship graph
                </button>
                <div className="verdict-group">
                  <span className="fb-label">Was this insight…</span>
                  <button className={`verdict-btn good ${fb.verdict === "useful" ? "on" : ""}`} onClick={() => setVerdict("useful")}><ThumbsUp size={13} /> Useful</button>
                  <button className={`verdict-btn ${fb.verdict === "not_useful" ? "on" : ""}`} onClick={() => setVerdict("not_useful")}><ThumbsDown size={13} /> Not useful</button>
                  <button className={`verdict-btn bad ${fb.verdict === "incorrect" ? "on" : ""}`} onClick={() => setVerdict("incorrect")}><X size={13} /> Incorrect</button>
                  <button className={`verdict-btn warn ${fb.verdict === "incomplete" ? "on" : ""}`} onClick={() => setVerdict("incomplete")}><HelpCircle size={13} /> Incomplete</button>
                  <button className="verdict-note-btn" onClick={() => setNoteOpen((v) => !v)}>{fb.note ? "Edit note" : "Add note"}</button>
                </div>
              </div>
              {noteOpen && (
                <div className="verdict-note">
                  <textarea value={fb.note} onChange={(e) => setFb({ ...fb, note: e.target.value })}
                    placeholder="Optional analyst note — what's missing, wrong, or worth tuning…" rows={2} />
                  <button className="btn-primary sm" onClick={saveNote}>Save note</button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </article>
  );
}
